import * as React from 'react';
import {
  Cluster,
  MarkerClusterer,
  Marker,
  SuperClusterAlgorithm,
} from '@googlemaps/markerclusterer';
import { useMapDisplayContext } from './MapDisplayContext';
import { PinMarker } from './PinMarker';
import { ReduxState } from 'ducks';
import { useSelector } from 'react-redux';
import { getFilteredPins } from './util';
import { ClusterRenderer } from './ClusterRenderer';
import { smoothMoveCamera } from './mapUtils';
import { useUrlQueryDisplayState } from './useUrlQueryDisplayState';
import { useItineraries } from './useItineraries';
import { useStarredPins } from 'hooks/useStarredPins';
import Map from 'ol/Map';

export interface Props {
  map: google.maps.Map;
  onClickPin: (pinKey: string) => void;
}

export const Pins = ({ map, onClickPin }: Props) => {
  const { activePinKey } = useUrlQueryDisplayState();

  const {
    searchText,
    displayMode,
    selectedFloor,
    activeItineraryPinKeys,
    activePage,
  } = useMapDisplayContext();
  const { selectedItinerary } = useItineraries();
  const { filters } = useUrlQueryDisplayState();
  const markers = React.useRef<{ [key: string]: Marker }>({});
  const [initializedMarkersCount, setInitializedMarkersCount] = React.useState(0);
  const clusterer = React.useRef<MarkerClusterer | null>(null);
  const digitalMap = useSelector((state: ReduxState) => state.universal.digitalMap.map);
  const allPins = digitalMap?.pins ?? [];
  const { isStarred } = useStarredPins();

  const activeGetDirectionsPinKey = displayMode === 'SHOW_DIRECTIONS_ROUTE' ? activePinKey : null;

  const filteredPins = React.useMemo(
    () =>
      activeGetDirectionsPinKey
        ? digitalMap?.pins?.filter((p) => p.key === activeGetDirectionsPinKey) ?? []
        : selectedItinerary && activePage === 'ITINERARIES.DETAILS'
        ? digitalMap?.pins?.filter((p) =>
            selectedItinerary.date_itineraries.find((date) =>
              date.items.find((item) => item.pin_key === p.key)
            )
          ) ?? []
        : getFilteredPins(
            digitalMap?.pins ?? [],
            filters,
            searchText,
            (digitalMap?.map_floor_count ?? 0) > 1 ? selectedFloor : undefined,
            isStarred
          ),
    [
      digitalMap,
      filters,
      searchText,
      activeGetDirectionsPinKey,
      selectedFloor,
      selectedItinerary,
      isStarred,
    ]
  );

  React.useEffect(() => {
    if (!clusterer.current) {
      clusterer.current = new MarkerClusterer({
        map,
        algorithm: new SuperClusterAlgorithm({
          maxZoom: 20 /* Note: this has no effect for SuperCluster when using WebGL vector maps like we do */,
          radius: 30,
        }),
        onClusterClick: (_: google.maps.MapMouseEvent, cluster: Cluster, m: google.maps.Map) => {
          const currentZoom = m.getZoom() ?? 0;
          const isGoogleMap = m instanceof google.maps.Map;
          smoothMoveCamera(isGoogleMap ? m : null, !isGoogleMap ? ((m as unknown) as Map) : null, {
            center: {
              lat: cluster.position.lat(),
              lng: cluster.position.lng(),
            },
            zoom: currentZoom + 1,
          });
        },
        renderer: new ClusterRenderer(),
      });
    }

    // Clear any existing markers when the clusterer is initialized
    clusterer.current.clearMarkers();
  }, [map]);

  const setMarkerRef = React.useCallback((marker: Marker | null, key: string) => {
    if (marker) {
      const existingMarker = markers.current[key];
      markers.current[key] = marker;

      // Ensure the marker is managed by the clusterer
      if (clusterer.current) {
        // Add to clusterer (this will automatically handle map management)
        clusterer.current.addMarker(marker);
      }

      if (!existingMarker) {
        setInitializedMarkersCount((prev) => prev + 1);
      }
    } else {
      const existingMarker = markers.current[key];
      if (existingMarker) {
        // Remove from clusterer if it exists
        if (clusterer.current) {
          clusterer.current.removeMarker(existingMarker);
        }
        delete markers.current[key];
        setInitializedMarkersCount((prev) => prev - 1);
      }
    }
  }, []);

  React.useEffect(() => {
    if (initializedMarkersCount !== digitalMap?.pins?.length) return;

    const visibleMarkers = Object.keys(markers.current)
      .filter((pinKey) => filteredPins.some((p) => p.key === pinKey))
      .map((key) => markers.current[key]);

    if (!clusterer.current) return;

    // First, remove all markers from the clusterer
    clusterer.current.clearMarkers();

    // Then add the filtered markers
    if (visibleMarkers.length > 0) {
      clusterer.current.addMarkers(visibleMarkers);
    }
  }, [digitalMap?.pins?.length, filteredPins, initializedMarkersCount]);

  return (
    <>
      {allPins.map((pin) => (
        <PinMarker
          key={pin.index}
          map={map}
          pin={pin}
          isActive={
            activePage === 'ITINERARIES.CREATE' || activePage === 'ITINERARIES.EDIT_DETAILS'
              ? activeItineraryPinKeys.includes(pin.key)
              : activePinKey === pin.key
          }
          onClick={onClickPin}
          ref={(marker) => setMarkerRef(marker, pin.key)}
        />
      ))}
    </>
  );
};
